import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { api } from '../../lib/api';
import { isoToDateInput, parseDateToIso } from '../../lib/format';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  venteId: number;
}

export function EcheanceWizardModal({ isOpen, onClose, venteId }: Props): JSX.Element {
  const queryClient = useQueryClient();
  const [nb, setNb] = useState('3');
  const [intervalle, setIntervalle] = useState('30');
  const [premier, setPremier] = useState(isoToDateInput(new Date().toISOString()));
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      const nbN = Number.parseInt(nb, 10);
      const intervalleN = Number.parseInt(intervalle, 10);
      const premierIso = parseDateToIso(premier);
      if (!Number.isFinite(nbN) || nbN < 2 || nbN > 12) {
        throw new Error("Nombre d'échéances entre 2 et 12");
      }
      if (!Number.isFinite(intervalleN) || intervalleN < 1) {
        throw new Error('Intervalle invalide');
      }
      if (premierIso === null) throw new Error('Date invalide');
      return api.echeances.wizard(venteId, {
        nb_echeances: nbN,
        intervalle_jours: intervalleN,
        premier_paiement_date: premierIso,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['echeances', venteId] });
      void queryClient.invalidateQueries({ queryKey: ['vente', venteId] });
      onClose();
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'Erreur';
      setError(msg === 'existing_echeances' ? 'Échéancier déjà créé' : msg);
    },
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Générer un échéancier"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annuler
          </Button>
          <Button
            onClick={() => {
              setError(null);
              mutation.mutate();
            }}
            disabled={mutation.isPending}
          >
            Générer
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Input
          label="Nombre d'échéances"
          type="number"
          min="2"
          max="12"
          value={nb}
          onChange={(e): void => setNb(e.target.value)}
        />
        <Input
          label="Intervalle entre échéances (jours)"
          type="number"
          min="1"
          value={intervalle}
          onChange={(e): void => setIntervalle(e.target.value)}
        />
        <Input
          label="Date de la 1re échéance"
          type="date"
          value={premier}
          onChange={(e): void => setPremier(e.target.value)}
        />
        <p className="text-xs text-slate-500">
          Le total TTC de la vente sera réparti à l'euro près, la dernière échéance compense
          l'arrondi.
        </p>
        {error !== null && <p className="text-xs text-red-400">{error}</p>}
      </div>
    </Modal>
  );
}
